import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import { useUI } from "@/redux/selectors/uiSelector";
import { useIntroStep } from "@/redux/selectors/modalSelector";
import { setChartTab, setTradeOrderView } from "@/redux/actions/ui";
import { TradeTabs } from "@/redux/interfaces/IUI";
import { NavigationTabs } from "./navigation-tabs/NavigationTabs";
import { AnalystForecastsView } from "./analytics/analyst-forecasts";
import { TechnicalAnalysisView } from "./analytics/technical-analysis";
import { useSymbolTechnicalAnalysis } from "./analytics/technical-analysis/hooks/useSymbolTechnicalAnalysis";
import { useSymbolAnalystForecasts } from "./analytics/analyst-forecasts/hooks/useSymbolAnalystForecasts";
import { useTipRanksTicker } from "./analytics/shared/hooks/useTipRanksTicker";
import TradingChart from "./trading-chart/TradingChart";
import TradingInfo from "./trading-info/TradingInfo";
import { TimeIndicator } from "./TimeIndicator";
import styles from "./TradingChartInfoSection.module.scss";

export const TradingChartInfoSection: React.FC = () => {
    const dispatch = useDispatch();
    const sectionRef = useRef<HTMLDivElement>(null);

    const { navTab, isTradeOrderViewOpen, isAssetsTableExpanded, isOpenTradesOpen } = useUI();
    const introStep = useIntroStep();
    const { ticker, tickerLabel } = useTipRanksTicker();

    const [forceChartReload, setForceChartReload] = useState(false);

    // UI state
    const isNarrow = isTradeOrderViewOpen && isAssetsTableExpanded;
    const isOverlayed = introStep === 4;

    // --- Analytics data
    const {
        data: techData,
        isLoading: isTechLoading,
        timeFrame,
        setTimeFrame,
    } = useSymbolTechnicalAnalysis({ isActive: navTab === TradeTabs.technicalAnalysis, ticker });

    const { data: analystData, isLoading: isAnalystLoading } = useSymbolAnalystForecasts({ ticker });

    // --- Available tabs
    const navigationTabs = useMemo(() => {
        if (!ticker) return [TradeTabs.chart, TradeTabs.info];
        return Object.values(TradeTabs).filter(
            tab => !(tab === TradeTabs.technicalAnalysis && !techData)
        );
    }, [ticker, techData]);

    // --- Handlers
    const changeTab = (tab: TradeTabs) => dispatch(setChartTab(tab));
    const openTradeOrder = () => dispatch(setTradeOrderView(true));

    // --- Effects
    useEffect(() => {
        if (!ticker) changeTab(TradeTabs.chart);
    }, [ticker]);

    useEffect(() => {
        if (introStep === 3) openTradeOrder();
        if (introStep === 4 && navTab === TradeTabs.info) changeTab(TradeTabs.chart);
    }, [introStep]);

    useEffect(() => {
        if (sectionRef.current && navTab === TradeTabs.chart) {
            const el = document.querySelector("prochart .layout1 ul.toolbar-adv>li");
            if (el?.classList.contains("hide-tlb-item")) {
                setForceChartReload(true);
                setTimeout(() => setForceChartReload(false), 100);
            }
        }
    }, [navTab]);

    return (
        <div
            ref={sectionRef}
            id="trading_chart_section"
            className={clsx(styles.section, {
                [styles.narrow]: isNarrow,
                [styles.overlayed]: isOverlayed,
            })}>
            {/* Header + Tabs */}
            <div className={clsx(styles.sectionHeader, { [styles.overlayed]: isOverlayed })}>
                <NavigationTabs
                    navTab={navTab}
                    navigationTabs={navigationTabs}
                    handleTabChange={changeTab}
                    isTradeOrderViewOpen={isTradeOrderViewOpen}
                    openTradeOrderView={openTradeOrder}
                />
                <div className={styles.dateTime}>
                    <TimeIndicator />
                </div>
            </div>

            {/* Content */}
            <div className={clsx(styles.content, { [styles.tradesTableOpen]: isOpenTradesOpen })}>
                <div
                    className={clsx(styles.tabContent, { [styles.hidden]: navTab !== TradeTabs.chart })}>
                    {!forceChartReload && <TradingChart wrapRef={sectionRef} />}
                </div>

                {navTab === TradeTabs.info && (
                    <div className={clsx(styles.tabContent, styles.scrollContent)}>
                        <TradingInfo />
                    </div>
                )}

                {navTab === TradeTabs.analystForecasts && ticker && (
                    <div
                        className={clsx(
                            styles.tabContent,
                            styles.scrollContent,
                            styles.analystForecasts
                        )}>
                        <AnalystForecastsView
                            ticker={ticker}
                            tickerLabel={tickerLabel}
                            isExpanded={!isTradeOrderViewOpen}
                            isActive
                            data={analystData}
                            isLoading={isAnalystLoading}
                        />
                    </div>
                )}

                {navTab === TradeTabs.technicalAnalysis && techData && (
                    <div
                        className={clsx(
                            styles.tabContent,
                            styles.scrollContent,
                            styles.technicalAnalysis
                        )}>
                        <TechnicalAnalysisView
                            ticker={ticker}
                            tickerLabel={tickerLabel}
                            isActive
                            data={techData}
                            isLoading={isTechLoading}
                            timeFrame={timeFrame}
                            // @ts-ignore
                            setTimeFrame={setTimeFrame}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
